import _cloneDeep from 'lodash-es/cloneDeep'

import type * as ecModels from '../../../../models'

import type { EditedEntity } from './edited-entity'

/**
 * A modification to an {@link EditedEntity}. Can either
 */
export type Edit<TEditedEntity extends EditedEntity<any>> =
  | // NOTE: we need to make sure to ONLY apply Readonly to Edit.Object, not
  // Edit.Callback due to this issue:
  // https://github.com/microsoft/TypeScript/issues/32566
  Readonly<Edit.Object<TEditedEntity>>
  | Edit.Callback<TEditedEntity>
export namespace Edit {
  /**
   * TODO: documentation and examples
   */
  export interface Object<TEditedEntity extends EditedEntity<any>> {
    // TODO: Figure out how to deal with the fact that user might start creation
    // with non of the required properties filled out (of the TCreateEntity),
    // but they have to fill out all the required properties before they can
    // submit
    input: Partial<TEditedEntity>
    /**
     * When `true`, the provided input is merged with the existing edited entity
     *
     * __NOTE:__ This is a shallow merge where top-level properties overwrite
     * existing top-level properties, even if they are objects containing nested
     * properties. For more nuanced merging, use a callback for the edit.
     *
     * @default true
     * @example
     * const edit = {
     *  input: { foo: { a: 1 } }
     *  merge: true
     * }
     * const currentEditedEntity = { foo: { b: 2 }, name: 'John' }
     * const mergedEditedEntity = { foo: { a: 1 }, name: 'John' }
     */
    merge?: boolean
  }
  /**
   * TODO: documentation and examples
   */
  export type Callback<TEditedEntity extends EditedEntity<any>> = (
    current: TEditedEntity | undefined,
  ) => TEditedEntity | void
  export namespace Callback {
    export type Required<TEditedEntity extends EditedEntity<any>> = (
      current: TEditedEntity,
    ) => TEditedEntity | void
  }

  /**
   * Gets the {@link EditedEntity} associated with this {@link Edit}
   */
  export type Entity<TEdit extends Edit<any>> =
    TEdit extends Edit<infer TEditedEntity> ? TEditedEntity : never

  /**
   * Simply an {@link Edit} paired with an {@link ecModels.EntityId}
   */
  export interface WithId<
    TId extends ecModels.EntityId,
    TEditedEntity extends EditedEntity<any>,
  > {
    /**
     * Identifies the entity being edited
     */
    id: TId
    /**
     * {@link Edit} being applied to the identified entity
     */
    edit: Edit<TEditedEntity>
  }

  /**
   * Produces an {@link EditedEntity} that is the result of applying an
   * {@link Edit} to an {@link EditedEntity}
   *
   * __NOTE:__ it is possible to end up with a Partial<{@link EditedEntity}>, in
   * case the edit is only a Partial<{@link EditedEntity}> and either the
   * original does not exist or {@link Edit.Object.merge} is set to `false`. It
   * is also possible to end up with `undefined`, in case the original does not
   * exist and the callback does not return an entity.
   */
  export function appliedTo<TEdit extends Edit<any>>(
    original: Entity<TEdit> | undefined,
    edit: TEdit,
  ): Partial<Entity<TEdit>> | undefined {
    if (typeof edit === 'function') {
      let edited = _cloneDeep(original)
      edited = edit(edited) ?? edited
      return edited
    }
    const { input, merge = true } = edit
    return merge && original ? { ...original, ...input } : input
  }
}
